"use client";

import React, { useState } from "react";
import { useTranslations, useLocale } from "next-intl";
import { Navbar } from "@/components/layout/navbar";
import { Footer } from "@/components/layout/footer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Trash2, AlertTriangle, CheckCircle2, Loader2, ArrowLeft } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { Link } from "@/i18n/navigation";

export default function DeleteAccountPage() {
    const t = useTranslations("DeleteAccount");
    const locale = useLocale();
    
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [reason, setReason] = useState("");
    const [confirm, setConfirm] = useState(false);
    
    const [errors, setErrors] = useState<{
        name?: string;
        email?: string;
        phone?: string;
        confirm?: string;
    }>({});
    
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [submitError, setSubmitError] = useState("");

    const validateForm = () => {
        const newErrors: typeof errors = {};
        
        if (!name.trim()) {
            newErrors.name = t("validation.name_required");
        }
        
        if (!email.trim()) {
            newErrors.email = t("validation.email_invalid");
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            newErrors.email = t("validation.email_invalid");
        }
        
        if (!phone.trim()) {
            newErrors.phone = t("validation.phone_required");
        }
        
        if (!confirm) {
            newErrors.confirm = t("validation.confirm_required");
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitError("");
        
        if (!validateForm()) return;
        
        setIsLoading(true);
        try {
            // Clean phone format
            const sanitizedPhone = phone.replace(/(?!\+)[^\d]/g, '');
            
            await api.submitDeleteAccountRequest({
                name,
                email,
                phone: sanitizedPhone,
                reason
            }, locale);
            
            setIsSubmitted(true);
        } catch (err: any) {
            console.error("Account deletion submission error:", err);
            setSubmitError(t("error_alert"));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex flex-col min-h-screen bg-slate-50 dark:bg-slate-950 text-foreground transition-colors duration-300">
            <Navbar />
            
            <main className="flex-grow pt-32 pb-24 relative overflow-hidden">
                {/* Decorative background blobs */}
                <div className="absolute top-0 left-0 w-full h-[500px] bg-gradient-to-b from-primary/5 to-transparent -z-10" />
                <div className="absolute bottom-[-10%] right-[-10%] w-[400px] h-[400px] bg-red-500/5 rounded-full blur-[120px] -z-10" />
                <div className="absolute top-[20%] left-[-10%] w-[350px] h-[350px] bg-secondary/5 rounded-full blur-[100px] -z-10" />

                <div className="container px-4 md:px-6 max-w-4xl mx-auto relative z-10">
                    <AnimatePresence mode="wait">
                        {!isSubmitted ? (
                            <motion.div
                                key="form-step"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.4 }}
                                className="space-y-8"
                            >
                                {/* Header Section */}
                                <div className="text-center space-y-4">
                                    <div className="inline-flex w-16 h-16 rounded-2xl bg-red-500/10 dark:bg-red-500/20 text-red-500 items-center justify-center mb-2 shadow-lg shadow-red-500/5 dark:shadow-none animate-pulse">
                                        <Trash2 className="w-8 h-8" />
                                    </div>
                                    <h1 className="text-3xl md:text-5xl font-extrabold font-heading text-primary dark:text-white tracking-tight">
                                        {t("title")}
                                    </h1>
                                    <p className="text-slate-500 dark:text-slate-400 text-lg max-w-2xl mx-auto">
                                        {t("subtitle")}
                                    </p>
                                </div>

                                {/* Warning Block */}
                                <div className="bg-red-50 dark:bg-red-950/20 border border-red-100 dark:border-red-900/30 rounded-3xl p-6 md:p-8 shadow-sm">
                                    <div className="flex gap-4 items-start">
                                        <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center text-red-500 shrink-0 mt-0.5">
                                            <AlertTriangle className="w-5 h-5" />
                                        </div>
                                        <div className="space-y-3">
                                            <h3 className="font-bold text-red-800 dark:text-red-300 text-lg md:text-xl">
                                                {t("warning_title")}
                                            </h3>
                                            <p className="text-red-700/90 dark:text-red-400/90 text-sm md:text-base leading-relaxed whitespace-pre-line">
                                                {t("warning_content")}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Form Section */}
                                <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-100 dark:border-white/5 p-8 md:p-12 shadow-xl shadow-black/[0.02]">
                                    <div className="mb-8">
                                        <h2 className="text-xl md:text-2xl font-bold text-primary dark:text-white">
                                            {t("form_title")}
                                        </h2>
                                        <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
                                            {t("form_desc")}
                                        </p>
                                    </div>

                                    {submitError && (
                                        <div className="mb-6 p-4 bg-rose-50 dark:bg-rose-950/20 text-rose-600 dark:text-rose-400 rounded-2xl text-sm border border-rose-100 dark:border-rose-900/20 font-medium">
                                            {submitError}
                                        </div>
                                    )}

                                    <form onSubmit={handleSubmit} className="space-y-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            {/* Full Name */}
                                            <div className="space-y-2">
                                                <Label htmlFor="name" className="text-slate-700 dark:text-slate-300 font-semibold">
                                                    {t("labels.name")} <span className="text-red-500">*</span>
                                                </Label>
                                                <Input
                                                    id="name"
                                                    type="text"
                                                    value={name}
                                                    onChange={(e) => {
                                                        setName(e.target.value);
                                                        if (errors.name) setErrors(prev => ({ ...prev, name: undefined }));
                                                    }}
                                                    placeholder={t("placeholders.name")}
                                                    className={`h-12 rounded-xl border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950 focus-visible:ring-red-500 focus-visible:border-red-500 ${errors.name ? 'border-red-500 dark:border-red-500' : ''}`}
                                                />
                                                {errors.name && (
                                                    <p className="text-red-500 text-xs mt-1 font-medium">{errors.name}</p>
                                                )}
                                            </div>

                                            {/* Phone Number */}
                                            <div className="space-y-2">
                                                <Label htmlFor="phone" className="text-slate-700 dark:text-slate-300 font-semibold">
                                                    {t("labels.phone")} <span className="text-red-500">*</span>
                                                </Label>
                                                <Input
                                                    id="phone"
                                                    type="tel"
                                                    value={phone}
                                                    onChange={(e) => {
                                                        setPhone(e.target.value);
                                                        if (errors.phone) setErrors(prev => ({ ...prev, phone: undefined }));
                                                    }}
                                                    placeholder="+20 12 26022004"
                                                    className={`h-12 rounded-xl border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950 focus-visible:ring-red-500 focus-visible:border-red-500 ${errors.phone ? 'border-red-500 dark:border-red-500' : ''}`}
                                                    dir="ltr"
                                                />
                                                {errors.phone && (
                                                    <p className="text-red-500 text-xs mt-1 font-medium">{errors.phone}</p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Registered Email */}
                                        <div className="space-y-2">
                                            <Label htmlFor="email" className="text-slate-700 dark:text-slate-300 font-semibold">
                                                {t("labels.email")} <span className="text-red-500">*</span>
                                            </Label>
                                            <Input
                                                id="email"
                                                type="email"
                                                value={email}
                                                onChange={(e) => {
                                                    setEmail(e.target.value);
                                                    if (errors.email) setErrors(prev => ({ ...prev, email: undefined }));
                                                }}
                                                placeholder={t("placeholders.email")}
                                                className={`h-12 rounded-xl border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950 focus-visible:ring-red-500 focus-visible:border-red-500 ${errors.email ? 'border-red-500 dark:border-red-500' : ''}`}
                                            />
                                            {errors.email && (
                                                <p className="text-red-500 text-xs mt-1 font-medium">{errors.email}</p>
                                            )}
                                        </div>

                                        {/* Reason for deletion */}
                                        <div className="space-y-2">
                                            <Label htmlFor="reason" className="text-slate-700 dark:text-slate-300 font-semibold">
                                                {t("labels.reason")}
                                            </Label>
                                            <Textarea
                                                id="reason"
                                                value={reason}
                                                onChange={(e) => setReason(e.target.value)}
                                                placeholder={t("placeholders.reason")}
                                                rows={4}
                                                className="rounded-xl border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950 focus-visible:ring-red-500 focus-visible:border-red-500 resize-none"
                                            />
                                        </div>

                                        {/* Confirmation Checkbox */}
                                        <div className="pt-2">
                                            <label className="flex items-start gap-3 cursor-pointer group">
                                                <input
                                                    type="checkbox"
                                                    checked={confirm}
                                                    onChange={(e) => {
                                                        setConfirm(e.target.checked);
                                                        if (errors.confirm) setErrors(prev => ({ ...prev, confirm: undefined }));
                                                    }}
                                                    className="w-5 h-5 rounded border-slate-300 text-red-600 focus:ring-red-500 dark:border-slate-800 mt-0.5 cursor-pointer accent-red-500"
                                                />
                                                <span className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed select-none group-hover:text-slate-900 dark:group-hover:text-slate-200 transition-colors font-medium">
                                                    {t("labels.confirm")}
                                                </span>
                                            </label>
                                            {errors.confirm && (
                                                <p className="text-red-500 text-xs mt-2 font-medium">{errors.confirm}</p>
                                            )}
                                        </div>

                                        {/* Submit Button */}
                                        <div className="pt-4">
                                            <Button
                                                type="submit"
                                                disabled={isLoading}
                                                className="w-full h-14 bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700 text-white rounded-xl font-bold transition-all flex items-center justify-center gap-2 shadow-lg shadow-red-600/10 active:scale-98"
                                            >
                                                {isLoading ? (
                                                    <>
                                                        <Loader2 className="w-5 h-5 animate-spin" />
                                                        {t("loading")}
                                                    </>
                                                ) : (
                                                    <>
                                                        <Trash2 className="w-5 h-5" />
                                                        {t("submit")}
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </form>
                                </div>
                            </motion.div>
                        ) : (
                            <motion.div
                                key="success-step"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.4, type: "spring", stiffness: 100 }}
                                className="max-w-2xl mx-auto text-center bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-100 dark:border-white/5 p-8 md:p-16 shadow-xl shadow-black/[0.02] space-y-8"
                            >
                                <div className="inline-flex w-20 h-20 rounded-full bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-500 items-center justify-center shadow-lg shadow-emerald-500/5">
                                    <CheckCircle2 className="w-12 h-12" />
                                </div>
                                
                                <div className="space-y-4">
                                    <h1 className="text-2xl md:text-4xl font-extrabold font-heading text-primary dark:text-white">
                                        {t("success_title")}
                                    </h1>
                                    <p className="text-slate-600 dark:text-slate-400 leading-relaxed md:text-lg">
                                        {t("success_desc")}
                                    </p>
                                </div>

                                <div className="pt-4">
                                    <Link href="/" className="inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 px-8 py-4 rounded-xl font-bold transition-all">
                                        <ArrowLeft className="w-5 h-5 rtl:rotate-180" />
                                        <span>{t("back_home")}</span>
                                    </Link>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </main>

            <Footer />
        </div>
    );
}
